import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Camera,
  CheckCircle2,
  ClipboardList,
  Clock,
  Download,
  FileText,
  Loader2,
  Paperclip,
  Upload,
  X,
} from 'lucide-react';

import { TASK_FILE_ACCEPT, TASK_MAX_FILE_MB, type TaskPublicInfo, type TaskSubmissionState } from '../../shared/tasks';
import { api, ApiError } from '../lib/api';
import {
  checkTaskFile,
  formatBytes,
  formatDue,
  forgetSubmission,
  openFile,
  saveSubmission,
  savedSubmission,
  taskApi,
  type SavedSubmission,
} from '../lib/tasks';
import { isPhoto, photosToPdf } from '../lib/photosToPdf';
import { formatGrade } from './ExamTake';

/**
 * Entregar una tarea.
 *
 * Solo se llega con el enlace de la tarea (o su QR). Sin curso con lista, se
 * escribe nombre y apellido; con lista, se elige el nombre y aparece la
 * práctica personal para descargar. Después se sube el trabajo en PDF o Word.
 * Este navegador recuerda la entrega: al volver se ve lo entregado, se puede
 * cambiar mientras no esté calificado y, cuando lo esté, la nota y el
 * comentario.
 */
export const TaskTake: React.FC<{
  initialCode?: string | null;
  defaultName?: { firstName: string; lastName: string } | null;
  onExit: () => void;
}> = ({ initialCode, defaultName, onExit }) => {
  const [info, setInfo] = useState<TaskPublicInfo | null>(null);
  const [saved, setSaved] = useState<SavedSubmission | null>(null);
  const [state, setState] = useState<TaskSubmissionState | null>(null);
  const [busy, setBusy] = useState(Boolean(initialCode));
  const [error, setError] = useState('');

  const lookup = useCallback(async (code: string) => {
    setBusy(true);
    setError('');
    try {
      // Si desde este navegador ya se entró, se enseña la entrega.
      const prev = savedSubmission(code);
      if (prev) {
        try {
          setState(await taskApi.state(prev));
          setSaved(prev);
          return;
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) forgetSubmission(code);
          else throw err;
        }
      }
      const data = await api.post<{ task: TaskPublicInfo }>('/task-access/lookup', { code });
      setInfo(data.task);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo abrir la tarea.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (initialCode) void lookup(initialCode);
  }, [initialCode, lookup]);

  const begin = async (who: { studentId: string } | { firstName: string; lastName: string }) => {
    if (!info) return;
    setBusy(true);
    setError('');
    try {
      const data = await taskApi.start({ code: info.code, ...who });
      const s = { submissionId: data.state.submissionId, token: data.token };
      saveSubmission(info.code, s);
      setSaved(s);
      setState(data.state);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo abrir la entrega.');
    } finally {
      setBusy(false);
    }
  };

  if (state && saved) {
    return (
      <Shell onBack={onExit} backLabel="Salir">
        <SubmissionPanel saved={saved} state={state} onChange={setState} />
      </Shell>
    );
  }

  if (info) {
    return (
      <Shell onBack={onExit} backLabel="Salir">
        <TaskHeader task={info} />
        {info.students ? (
          <StudentPicker students={info.students} busy={busy} error={error} onPick={(studentId) => void begin({ studentId })} />
        ) : !info.isOpen ? (
          <Notice>Esta tarea está cerrada y ya no admite entregas.</Notice>
        ) : (
          <NameForm busy={busy} error={error} defaultName={defaultName} onSubmit={(firstName, lastName) => void begin({ firstName, lastName })} />
        )}
      </Shell>
    );
  }

  // Sin enlace válido no hay forma de entrar: no se puede escribir un código.
  return (
    <div className="max-w-md mx-auto px-4 py-16 space-y-6">
      <button onClick={onExit} className="flex items-center gap-1.5 text-ink-400 hover:text-ink-100 text-sm">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <div className="surface-raised p-6 sm:p-8 space-y-4 text-center">
        <span className="inline-flex p-3 rounded-xl bg-magenta-500/15 text-magenta-400">
          <ClipboardList className="w-7 h-7" />
        </span>
        {busy ? (
          <Loader2 className="w-6 h-6 animate-spin text-ink-400 mx-auto" />
        ) : (
          <>
            <h1 className="text-2xl font-display font-bold">
              {initialCode ? 'Este enlace no es de ninguna tarea' : 'Para entregar una tarea necesitas su enlace'}
            </h1>
            <p className="text-ink-300 text-sm">
              Ábrela desde el enlace o el código QR que te dio tu profesor o profesora. Si no funciona, pídele que te
              lo vuelva a enviar.
            </p>
            {error && initialCode && !/enlace/.test(error) && <p className="text-danger-400 text-sm">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
};

/* ================================================================ */

const Shell: React.FC<{ onBack: () => void; backLabel: string; children: React.ReactNode }> = ({
  onBack,
  backLabel,
  children,
}) => (
  <div className="max-w-2xl mx-auto px-4 py-10 space-y-6">
    <button onClick={onBack} className="flex items-center gap-1.5 text-ink-400 hover:text-ink-100 text-sm">
      <ArrowLeft className="w-4 h-4" /> {backLabel}
    </button>
    <div className="surface-raised p-6 sm:p-8 space-y-5">{children}</div>
  </div>
);

const Notice: React.FC<{ children: React.ReactNode; tone?: 'danger' | 'warn' }> = ({ children, tone = 'danger' }) => (
  <p
    className={`flex items-start gap-2 rounded-xl border p-3 text-sm ${
      tone === 'danger'
        ? 'border-danger-500/40 bg-danger-500/10 text-danger-400'
        : 'border-ember-500/40 bg-ember-500/10 text-ember-300'
    }`}
  >
    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
    <span>{children}</span>
  </p>
);

const TaskHeader: React.FC<{ task: TaskPublicInfo; serverNow?: number }> = ({ task, serverNow = Date.now() }) => {
  const overdue = task.dueAt !== null && serverNow > task.dueAt;
  const [error, setError] = useState('');
  return (
    <div className="space-y-4">
      <div>
        <p className="text-ink-500 text-xs uppercase tracking-wider">
          Tarea{task.courseName ? ` · ${task.courseName}` : ''}
        </p>
        <h1 className="text-2xl font-display font-bold mt-1">{task.title}</h1>
        {task.creatorName && <p className="text-ink-400 text-sm mt-1">De {task.creatorName}</p>}
      </div>

      {task.dueAt !== null && (
        <p className={`flex items-center gap-2 text-sm ${overdue ? 'text-ember-300' : 'text-ink-300'}`}>
          <Clock className="w-4 h-4" />
          Fecha de entrega: {formatDue(task.dueAt)}
          {overdue && (task.acceptLate ? ' · ya pasó (se marcará con retraso)' : ' · ya pasó')}
        </p>
      )}

      {task.instructions && (
        <p className="text-ink-200 text-sm whitespace-pre-wrap rounded-xl bg-ink-850 border border-ink-700 p-3">
          {task.instructions}
        </p>
      )}

      {task.attachmentName && (
        <button
          onClick={() =>
            void openFile(task.attachmentName!, () => taskApi.attachment(task.code)).catch(() =>
              setError('No se pudo abrir el archivo.'),
            )
          }
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-ink-700 bg-ink-850 hover:bg-ink-800 text-sm text-left"
        >
          <Paperclip className="w-4 h-4 text-sky-400 shrink-0" />
          <span className="flex-1 min-w-0 truncate">{task.attachmentName}</span>
          <span className="text-ink-500 text-xs">{formatBytes(task.attachmentSize)} · Abrir</span>
        </button>
      )}
      {error && <p className="text-danger-400 text-sm">{error}</p>}
    </div>
  );
};

/** Tarea de un curso con lista: cada uno busca y elige su nombre. */
const StudentPicker: React.FC<{
  students: { id: string; name: string }[];
  busy: boolean;
  error: string;
  onPick: (studentId: string) => void;
}> = ({ students, busy, error, onPick }) => {
  const [chosen, setChosen] = useState('');
  const chosenName = students.find((s) => s.id === chosen)?.name;
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (chosen && !busy) onPick(chosen);
      }}
    >
      <p className="text-sm text-ink-300">¿Quién eres? Elige tu nombre para ver tu práctica y entregar tu trabajo.</p>
      <div role="radiogroup" aria-label="Tu nombre" className="grid sm:grid-cols-2 gap-2">
        {students.map((s) => (
          <label
            key={s.id}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer text-sm transition-colors ${
              chosen === s.id ? 'border-sky-500 bg-sky-500/10 text-ink-50' : 'border-ink-700 bg-ink-850 hover:bg-ink-800'
            }`}
          >
            <input
              type="radio"
              name="student"
              value={s.id}
              checked={chosen === s.id}
              onChange={() => setChosen(s.id)}
              className="accent-sky-500"
            />
            {s.name}
          </label>
        ))}
      </div>
      <p className="text-ink-500 text-xs">
        Elige tu propio nombre: la práctica de cada uno es distinta, y lo que entregues quedará a ese nombre.
      </p>
      {error && <p className="text-danger-400 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={!chosen || busy}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold bg-ember-500 hover:bg-ember-400
                   text-on-accent disabled:bg-ink-800 disabled:text-ink-500 transition-colors"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
        {chosenName ? `Soy ${chosenName}` : 'Continuar'}
      </button>
    </form>
  );
};

const NameForm: React.FC<{
  busy: boolean;
  error: string;
  defaultName?: { firstName: string; lastName: string } | null;
  onSubmit: (firstName: string, lastName: string) => void;
}> = ({ busy, error, defaultName, onSubmit }) => {
  const [firstName, setFirstName] = useState(defaultName?.firstName ?? '');
  const [lastName, setLastName] = useState(defaultName?.lastName ?? '');
  const ready = firstName.trim().length >= 2 && lastName.trim().length >= 2;
  const field =
    'w-full h-11 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 outline-none focus:border-sky-500';
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready && !busy) onSubmit(firstName.trim(), lastName.trim());
      }}
    >
      <p className="text-sm text-ink-300">Pon tu nombre real: así sabrá de quién es la entrega.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="space-y-1">
          <span className="text-sm text-ink-300">Nombre</span>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value.slice(0, 40))}
            autoComplete="given-name"
            className={field}
          />
        </label>
        <label className="space-y-1">
          <span className="text-sm text-ink-300">Apellido</span>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value.slice(0, 40))}
            autoComplete="family-name"
            className={field}
          />
        </label>
      </div>
      {error && <p className="text-danger-400 text-sm">{error}</p>}
      <button
        type="submit"
        disabled={!ready || busy}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold bg-ember-500 hover:bg-ember-400
                   text-on-accent disabled:bg-ink-800 disabled:text-ink-500 transition-colors"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
        Continuar
      </button>
    </form>
  );
};

/* ================================================================
 * La entrega
 * ================================================================ */

const SubmissionPanel: React.FC<{
  saved: SavedSubmission;
  state: TaskSubmissionState;
  onChange: (s: TaskSubmissionState) => void;
}> = ({ saved, state, onChange }) => {
  const [file, setFile] = useState<File | null>(null);
  /** Fotos de las hojas hechas a mano: al entregar se unen en un PDF. */
  const [photos, setPhotos] = useState<File[]>([]);
  const [note, setNote] = useState(state.note);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [preparing, setPreparing] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const graded = state.grade !== null || Boolean(state.feedback);
  const fullName = `${state.firstName} ${state.lastName}`.trim();

  /** Un PDF o un Word, o una o varias fotos (que se suman a las ya elegidas). */
  const pick = (list: FileList | File[] | null | undefined) => {
    const files = [...(list ?? [])];
    if (files.length === 0) return;
    if (files.every(isPhoto)) {
      setError('');
      setFile(null);
      setPhotos((prev) => [...prev, ...files].slice(0, 40));
      return;
    }
    if (files.length > 1) return setError('Elige un solo PDF o Word, o solo fotos.');
    const problem = checkTaskFile(files[0]);
    if (problem) return setError(`${problem} También puedes subir fotos de tus hojas.`);
    setError('');
    setPhotos([]);
    setFile(files[0]);
  };

  const submit = async () => {
    if (!file && photos.length === 0) return;
    setBusy(true);
    setError('');
    try {
      let toSend = file;
      if (!toSend) {
        toSend = await photosToPdf(photos, `Entrega - ${fullName}.pdf`, (done, total) =>
          setPreparing(done < total ? `Preparando las fotos… ${done + 1} de ${total}` : 'Fotos listas'),
        );
        setPreparing(null);
        if (toSend.size > TASK_MAX_FILE_MB * 1024 * 1024) {
          throw new Error(`Las fotos juntas pasan de ${TASK_MAX_FILE_MB} MB. Entrega menos fotos o de menos páginas.`);
        }
      }
      setProgress(0);
      onChange(await taskApi.upload(saved, toSend, note, setProgress));
      setFile(null);
      setPhotos([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo subir el archivo.');
    } finally {
      setBusy(false);
      setProgress(null);
      setPreparing(null);
    }
  };

  const withdraw = async () => {
    if (!confirm('¿Anular la entrega? Se borrará el archivo y tendrás que volver a subirlo.')) return;
    setBusy(true);
    try {
      onChange(await taskApi.withdraw(saved));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo anular.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <TaskHeader task={state.task} serverNow={state.serverNow} />

      {state.material && (
        <button
          onClick={() =>
            void openFile(state.material!.name, () => taskApi.ownMaterial(saved)).catch(() =>
              setError('No se pudo descargar tu práctica.'),
            )
          }
          className="w-full flex items-center gap-3 rounded-xl border-2 border-sky-500/50 bg-sky-500/10 hover:bg-sky-500/15 p-4 text-left transition-colors"
        >
          <Download className="w-6 h-6 text-sky-400 shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block text-xs uppercase tracking-wider text-sky-400">Práctica de {fullName}</span>
            <span className="block font-bold text-ink-50">Descarga tu práctica</span>
            <span className="block text-ink-400 text-xs truncate">
              {state.material.name} · {formatBytes(state.material.size)}
            </span>
          </span>
        </button>
      )}

      <div className="h-px bg-ink-700" />

      <div className="space-y-0.5">
        <h2 className="font-bold">Tu entrega</h2>
        <p className="text-ink-400 text-sm">
          A nombre de <strong className="text-ink-100">{fullName}</strong>
        </p>
      </div>

      {graded && (
        <div className="rounded-xl border border-success-500/40 bg-success-500/10 p-4 space-y-2">
          {state.grade !== null && (
            <p className="text-success-400 font-bold text-lg">
              Nota: {formatGrade(state.grade)} / {state.gradeScale}
            </p>
          )}
          {state.feedback && (
            <p className="text-ink-200 text-sm whitespace-pre-wrap">
              <span className="text-ink-400">Comentario: </span>
              {state.feedback}
            </p>
          )}
        </div>
      )}

      {state.submittedAt && state.fileName ? (
        <div className="flex items-center gap-3 rounded-xl border border-ink-700 bg-ink-850 p-3">
          <CheckCircle2 className={`w-5 h-5 shrink-0 ${state.late ? 'text-ember-300' : 'text-success-400'}`} />
          <div className="flex-1 min-w-0">
            <button
              onClick={() =>
                void openFile(state.fileName!, () => taskApi.ownFile(saved)).catch(() =>
                  setError('No se pudo abrir el archivo.'),
                )
              }
              className="font-semibold text-sm text-sky-400 hover:underline truncate block max-w-full text-left"
            >
              {state.fileName}
            </button>
            <p className="text-ink-500 text-xs">
              {state.late ? 'Entregada con retraso' : 'Entregada'} el {new Date(state.submittedAt).toLocaleString('es')}{' '}
              · {formatBytes(state.fileSize)}
            </p>
          </div>
          {state.canSubmit && (
            <button
              onClick={() => void withdraw()}
              disabled={busy}
              className="text-xs text-ink-400 hover:text-danger-400 shrink-0"
            >
              Anular
            </button>
          )}
        </div>
      ) : (
        <p className="text-ink-400 text-sm">Aún no has entregado nada.</p>
      )}

      {state.blockedReason && !graded && <Notice tone="warn">{state.blockedReason}</Notice>}

      {state.canSubmit && (
        <div className="space-y-3">
          <div
            role="button"
            tabIndex={0}
            onClick={() => input.current?.click()}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              pick(e.dataTransfer.files);
            }}
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
              dragging ? 'border-sky-400 bg-sky-500/10' : 'border-ink-700 hover:border-ink-500'
            }`}
          >
            {file ? (
              <>
                <FileText className="w-8 h-8 text-sky-400" />
                <p className="font-semibold text-sm break-all">{file.name}</p>
                <p className="text-ink-500 text-xs">{formatBytes(file.size)} · toca para elegir otro</p>
              </>
            ) : photos.length > 0 ? (
              <>
                <Camera className="w-8 h-8 text-sky-400" />
                <p className="font-semibold text-sm">
                  {photos.length} {photos.length === 1 ? 'foto' : 'fotos'} · se entregan como un solo PDF
                </p>
                <p className="text-ink-500 text-xs">Toca para añadir más hojas</p>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8 text-ink-400" />
                <p className="font-semibold text-sm">
                  {state.submittedAt ? 'Sube otro archivo para reemplazarlo' : 'Elige tu trabajo o arrástralo aquí'}
                </p>
                <p className="text-ink-500 text-xs">
                  Fotos de tus hojas (una por página), un PDF o un Word · hasta {TASK_MAX_FILE_MB} MB
                </p>
              </>
            )}
            <input
              ref={input}
              type="file"
              multiple
              accept={`${TASK_FILE_ACCEPT},image/*`}
              className="hidden"
              onChange={(e) => {
                pick(e.target.files);
                e.target.value = '';
              }}
            />
          </div>

          <label className="block space-y-1">
            <span className="text-sm text-ink-300">Comentario (opcional)</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              placeholder="Algo que quieras decirle a tu profesor"
              className="w-full h-11 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 outline-none focus:border-sky-500"
            />
          </label>

          {photos.length > 0 && (
            <ol className="space-y-1">
              {photos.map((p, i) => (
                <li key={`${p.name}-${i}`} className="flex items-center gap-2 text-xs text-ink-300">
                  <span className="text-ink-500 tabular-nums w-14 shrink-0">Página {i + 1}</span>
                  <span className="flex-1 min-w-0 truncate">{p.name}</span>
                  <button
                    onClick={() => setPhotos((prev) => prev.filter((_, k) => k !== i))}
                    disabled={busy}
                    aria-label={`Quitar la página ${i + 1}`}
                    className="p-1 text-ink-500 hover:text-danger-400"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ol>
          )}

          {preparing && <p className="text-sky-400 text-xs text-center" role="status">{preparing}</p>}
          {progress !== null && (
            <div className="space-y-1" role="status">
              <div className="h-2 rounded-full bg-ink-800 overflow-hidden">
                <div className="h-full bg-sky-500 transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
              <p className="text-ink-400 text-xs text-center">
                {progress < 1
                  ? `Subiendo… ${Math.round(progress * 100)} %. No cierres esta página.`
                  : 'Guardando en el servidor…'}
              </p>
            </div>
          )}
          {error && <p className="text-danger-400 text-sm">{error}</p>}
          <button
            onClick={() => void submit()}
            disabled={(!file && photos.length === 0) || busy}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold bg-ember-500 hover:bg-ember-400
                       text-on-accent disabled:bg-ink-800 disabled:text-ink-500 transition-colors"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
            {state.submittedAt ? 'Reemplazar entrega' : 'Entregar'}
          </button>
          <p className="text-ink-500 text-xs text-center">Se entrega a nombre de {fullName}.</p>
        </div>
      )}
      {!state.canSubmit && error && <p className="text-danger-400 text-sm">{error}</p>}

      <p className="text-ink-500 text-xs">
        Vuelve a abrir el mismo enlace para ver tu entrega y tu nota. Desde otro dispositivo, elige otra vez tu nombre.
      </p>
    </div>
  );
};
